import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  apiErrorDetail,
  type ArtifactPublicView,
  type ArtifactVersion,
} from "../lib/api";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(s: string): string {
  return new Date(s).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Version history page for a single artifact. Owner or editor. */
export function ArtifactVersionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [artifact, setArtifact] = useState<ArtifactPublicView | null>(null);
  const [versions, setVersions] = useState<ArtifactVersion[] | null>(null);
  /** Load failures only — this one replaces the whole view. */
  const [error, setError] = useState<string | null>(null);
  /** Restore failures — rendered inline so the list and the note survive. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!slug) return;
    setError(null);
    Promise.all([api.getOne(slug), api.listVersions(slug)])
      .then(([art, vers]) => {
        setArtifact(art);
        setVersions(vers);
      })
      .catch((e: Error) => setError(e.message));
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Restore one version, carrying the page's note.
   *
   * No ``ifVersion`` on purpose: restore is server-side exempt from the
   * token (an inherent clobber, and the pre-restore snapshot preserves the
   * displaced state), and this page IS the clobber-recovery flow — the
   * artifact was typically just modified, so a threaded token would be
   * stale and guarantee a wasted 409 on the first click.
   *
   * Failures land in ``actionError``, never in ``error`` — the latter replaces
   * the whole view, which would unmount the very list (and note) the user
   * needs to retry. The server 422 names when a note is required.
   */
  async function restore(versionId: string) {
    if (!slug) return;
    setRestoring(versionId);
    setSuccessMsg(null);
    setActionError(null);
    try {
      await api.restoreVersion(slug, versionId, { revisionNote });
      // Don't claim a snapshot was taken — backend may proceed without one
      // when the prior live state was already partial (recovery case).
      setSuccessMsg("Version restored.");
      setRevisionNote("");
      load();
    } catch (e) {
      // Session expiry navigates away via call()'s sentinel — don't flash it
      // as an action error (same filter ShareModal uses).
      if ((e as Error).message !== "redirecting-to-login") {
        setActionError(apiErrorDetail(e));
      }
    } finally {
      setRestoring(null);
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="text-rose-700">Error: {error}</p>
      </div>
    );
  }

  if (!slug || versions === null || artifact === null) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="text-slate-500">Loading…</p>
      </div>
    );
  }

  return (
    <div>
      <BackLink />

      <header className="mb-6 mt-4">
        <h1 className="text-2xl font-semibold">Version history</h1>
        <p className="mt-1 text-sm text-slate-600">
          <span className="font-medium">{artifact.title}</span>
          <span className="ml-1 font-mono text-slate-400 text-xs">{slug}</span>
        </p>
      </header>

      {successMsg && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {successMsg}
        </div>
      )}

      {actionError && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {actionError}
        </div>
      )}

      {versions.length > 0 && (
        <label className="mb-4 block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Revision note
          </span>
          <input
            type="text"
            value={revisionNote}
            onChange={(e) => setRevisionNote(e.target.value)}
            placeholder="Why are you restoring? (required once the artifact has editors)"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
        </label>
      )}

      {versions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center">
          <p className="text-sm text-slate-600">
            No versions yet. Versions are created automatically on every
            file-changing update.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">By</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2">Size</th>
                <th className="px-3 py-2">Files</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {versions.map((v) => (
                <tr key={v.version_id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                    {formatDate(v.created_at)}
                    {v.restored_from_version_id && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-xs text-amber-700">
                        pre-restore
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{v.actor_email}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {v.revision_note ? (
                      v.revision_note
                    ) : (
                      <span className="italic text-slate-400">no note</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                    {formatBytes(v.size_bytes)}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{v.files.length}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <a
                        href={`/a/${slug}/versions/${v.version_id}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        View
                      </a>
                      <button
                        type="button"
                        onClick={() => restore(v.version_id)}
                        disabled={restoring === v.version_id}
                        className="rounded bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700 disabled:opacity-50"
                      >
                        {restoring === v.version_id ? "Restoring…" : "Restore"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/mine" className="text-sm text-slate-500 hover:text-slate-700">
      ← Back to my artifacts
    </Link>
  );
}
